import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getChildrenByLineID } from '@/api/getChildByLineID';
import { LineUserAtom } from '@/jotai/jotai';
import { Child } from '@/types/child';

const UserSupportPlanChildrenPage = () => {
  const [children, setChildren] = useState<Child[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lineUser] = useAtom(LineUserAtom);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChildren = async () => {
      setIsLoading(true);

      try {
        // 保護者に関連付けされている子供情報を取得する
        // 取得した子供情報をsetChildren関数を使ってstateにセットする
        // エラーが発生した場合は、エラーメッセージをsetError関数を使ってstateにセットする

        const response = await getChildrenByLineID(
          lineUser?.lineID ?? '',
          lineUser?.accessToken ?? ''
        );

        if (response.status === 200 && response.result) {
          setChildren(response.result);
        }
      } catch (err) {
        console.error('子供データの取得に失敗しました:', err);
        setError('データの読み込みに失敗しました。再度お試しください。');
      } finally {
        setIsLoading(false);
      }
    };
    if (lineUser) {
      fetchChildren();
    }
  }, [lineUser]);

  const handleChildSelect = (childId: number) => {
    // 選択した子供の個別支援記録ページへ遷移
    navigate(`/user/support-plans/children/${childId}`);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-500">データを読み込み中...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-red-500">{error}</div>
          <button
            className="mt-4 rounded bg-blue-500 px-4 py-2 text-white"
            onClick={() => window.location.reload()}
          >
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* ヘッダー */}
      <div className="border-b px-4 py-3 text-xl font-bold">個別支援計画(お子様選択)</div>

      {/* 説明テキスト */}
      <div className="px-4 py-3 text-sm text-gray-600">
        個別支援計画を確認する子供を選択してください
      </div>

      {/* 子供一覧 */}
      <div className="px-4">
        <ul className="space-y-3">
          {children.map((child) => (
            <li
              key={child.id}
              className="overflow-hidden rounded-lg border shadow-sm"
              onClick={() => handleChildSelect(child.id)}
            >
              <div className="flex cursor-pointer items-center p-4">
                <div className="flex-1">
                  <h2 className="flex items-center text-base font-medium">
                    {child.last_name} {child.first_name}
                  </h2>
                </div>
                <div className="text-gray-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {children.length === 0 && (
        <div className="px-4 py-8 text-center text-gray-500">表示できる子供データがありません</div>
      )}

      {/* 固定フッター */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-4">
        <button
          className="block w-full py-2 text-center text-base text-blue-700 underline"
          onClick={() => navigate('/')}
        >
          ホームに戻る
        </button>
      </div>
    </div>
  );
};

export default UserSupportPlanChildrenPage;
